// src/pages/Appointments/AppointmentForm/AppointmentFormSections.jsx
import React, { useState } from "react";
import {
  FiMapPin,
  FiCalendar,
  FiClock,
  FiInfo,
  FiDollarSign,
  FiHeart,
  FiShoppingBag,
  FiUser,
  FiPhone,
  FiMail,
  FiCheck,
  FiAlertCircle,
  FiEdit,
} from "react-icons/fi";
import UserSelection from "pages/User/components/UserSelection";
import toast from "react-hot-toast";

// ============= BRANCH SELECTION SECTION =============
export const BranchSelectionSection = ({
  formData,
  errors,
  onChange,
  branches,
}) => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="flex items-center gap-2 mb-4">
      <FiMapPin className="text-primary text-xl" />
      <h3 className="text-lg font-semibold text-text">Select Branch</h3>
    </div>

    <div className="space-y-4">
      {/* Branch Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Branch *
        </label>
        <select
          name="branchCode"
          value={formData.branchCode}
          onChange={onChange}
          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.branchCode ? "border-red-500" : "border-gray-300"
          }`}
        >
          <option value="">Select a branch</option>
          {branches?.map((branch) => (
            <option key={branch.code} value={branch.code}>
              {branch.name} - {branch.address?.city}
            </option>
          ))}
        </select>
        {errors.branchCode && (
          <p className="mt-1 text-sm text-red-500">{errors.branchCode}</p>
        )}
      </div>

      {/* Branch Details (if selected) */}
      {formData.branchCode && branches && (
        <div className="bg-neutral-light rounded-lg p-4">
          {(() => {
            const selectedBranch = branches.find(
              (b) => b.code === formData.branchCode
            );
            if (!selectedBranch) return null;

            return (
              <div className="space-y-2">
                <h4 className="font-medium text-text">{selectedBranch.name}</h4>
                <p className="text-sm text-neutral-dark">
                  {selectedBranch.address?.street},{" "}
                  {selectedBranch.address?.city},{" "}
                  {selectedBranch.address?.state}{" "}
                  {selectedBranch.address?.pincode}
                </p>
                {selectedBranch.contact?.phone && (
                  <p className="text-sm text-neutral-dark">
                    <FiPhone className="inline mr-1" />
                    {selectedBranch.contact.phone}
                  </p>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  </div>
);

// ============= APPOINTMENT TYPE SECTION =============
export const AppointmentTypeSection = ({ formData, errors, onChange }) => {
  const appointmentTypes = [
    {
      value: "PRODUCT_CONSULTATION",
      label: "Product Consultation",
      description: "Discuss products with our expert",
      duration: 30,
      icon: FiShoppingBag,
    },
    {
      value: "CUSTOM_DESIGN",
      label: "Custom Design",
      description: "Create your unique jewelry",
      duration: 60,
      icon: FiHeart,
    },
    {
      value: "REPAIR_CONSULTATION",
      label: "Repair Consultation",
      description: "Get repair estimates",
      duration: 30,
      icon: FiInfo,
    },
    {
      value: "APPRAISAL",
      label: "Appraisal",
      description: "Get jewelry appraised",
      duration: 45,
      icon: FiDollarSign,
    },
    {
      value: "ENGAGEMENT_RING",
      label: "Engagement Ring",
      description: "Find the perfect ring",
      duration: 60,
      icon: FiHeart,
    },
    {
      value: "WEDDING_JEWELRY",
      label: "Wedding Jewelry",
      description: "Complete bridal collection",
      duration: 60,
      icon: FiHeart,
    },
    {
      value: "GENERAL_INQUIRY",
      label: "General Inquiry",
      description: "General questions and browsing",
      duration: 20,
      icon: FiInfo,
    },
    {
      value: "VIP_CLIENT",
      label: "VIP Client",
      description: "Premium personalized service",
      duration: 90,
      icon: FiUser,
    },
  ];

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center gap-2 mb-4">
        <FiCalendar className="text-primary text-xl" />
        <h3 className="text-lg font-semibold text-text">Appointment Type</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {appointmentTypes.map((type) => {
          const Icon = type.icon;
          return (
            <label
              key={type.value}
              className={`relative cursor-pointer rounded-lg border-2 p-4 transition-all hover:border-primary
                ${
                  formData.type === type.value
                    ? "border-primary bg-primary-light bg-opacity-10"
                    : "border-gray-200"
                }`}
            >
              <input
                type="radio"
                name="type"
                value={type.value}
                checked={formData.type === type.value}
                onChange={onChange}
                className="sr-only"
              />
              <div className="flex flex-col">
                <div className="flex items-center justify-between mb-2">
                  <Icon
                    className={`text-2xl ${
                      formData.type === type.value
                        ? "text-primary"
                        : "text-neutral-dark"
                    }`}
                  />
                  {formData.type === type.value && (
                    <FiCheck className="text-primary" />
                  )}
                </div>
                <h4 className="font-medium text-text mb-1">{type.label}</h4>
                <p className="text-sm text-neutral-dark mb-2">
                  {type.description}
                </p>
                <p className="text-xs text-neutral-dark flex items-center gap-1">
                  <FiClock size={12} />
                  {type.duration} mins
                </p>
              </div>
            </label>
          );
        })}
      </div>

      {errors.type && (
        <p className="mt-2 text-sm text-red-500">{errors.type}</p>
      )}
    </div>
  );
};

// ============= INTENT SECTION =============
export const IntentSection = ({
  formData,
  errors,
  onChange,
  onNestedChange,
  onArrayChange,
}) => {
  const purposes = [
    { value: "BROWSING", label: "Just Browsing" },
    { value: "BUYING", label: "Ready to Buy" },
    { value: "CUSTOM_DESIGN", label: "Custom Design" },
    { value: "EXCHANGE_VALUATION", label: "Exchange Valuation" },
    { value: "REPAIR_INQUIRY", label: "Repair Inquiry" },
    { value: "GIFT_SHOPPING", label: "Gift Shopping" },
    { value: "WEDDING_PLANNING", label: "Wedding Planning" },
    { value: "INVESTMENT", label: "Investment" },
  ];

  const occasions = [
    { value: "ENGAGEMENT", label: "Engagement" },
    { value: "WEDDING", label: "Wedding" },
    { value: "ANNIVERSARY", label: "Anniversary" },
    { value: "BIRTHDAY", label: "Birthday" },
    { value: "FESTIVAL", label: "Festival" },
    { value: "GIFT", label: "Gift" },
    { value: "SELF", label: "Self" },
    { value: "INVESTMENT", label: "Investment" },
    { value: "OTHER", label: "Other" },
  ];

  const metals = ["GOLD", "SILVER", "PLATINUM", "DIAMOND"];
  const styles = [
    "TRADITIONAL",
    "MODERN",
    "CONTEMPORARY",
    "FUSION",
    "MINIMALIST",
  ];
  const categories = [
    "NECKLACE",
    "EARRINGS",
    "RINGS",
    "BANGLES",
    "PENDANTS",
    "BRACELETS",
  ];

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center gap-2 mb-4">
        <FiInfo className="text-primary text-xl" />
        <h3 className="text-lg font-semibold text-text">Visit Details</h3>
      </div>

      <div className="space-y-6">
        {/* Purpose */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Purpose of Visit *
          </label>
          <select
            value={formData.intent.purpose}
            onChange={(e) => onNestedChange("intent.purpose", e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
              errors["intent.purpose"] ? "border-red-500" : "border-gray-300"
            }`}
          >
            <option value="">Select purpose</option>
            {purposes.map((purpose) => (
              <option key={purpose.value} value={purpose.value}>
                {purpose.label}
              </option>
            ))}
          </select>
          {errors["intent.purpose"] && (
            <p className="mt-1 text-sm text-red-500">
              {errors["intent.purpose"]}
            </p>
          )}
        </div>

        {/* Occasion */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Occasion
            </label>
            <select
              value={formData.intent.occasion}
              onChange={(e) =>
                onNestedChange("intent.occasion", e.target.value)
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Select occasion</option>
              {occasions.map((occasion) => (
                <option key={occasion.value} value={occasion.value}>
                  {occasion.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Occasion Date
            </label>
            <input
              type="date"
              value={formData.intent.occasionDate}
              onChange={(e) =>
                onNestedChange("intent.occasionDate", e.target.value)
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        {/* Budget */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Budget Range (₹)
          </label>
          <div className="grid grid-cols-2 gap-4">
            <input
              type="number"
              placeholder="Minimum"
              value={formData.intent.budget.min}
              onChange={(e) =>
                onNestedChange("intent.budget.min", e.target.value)
              }
              className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <input
              type="number"
              placeholder="Maximum"
              value={formData.intent.budget.max}
              onChange={(e) =>
                onNestedChange("intent.budget.max", e.target.value)
              }
              className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <label className="flex items-center mt-2">
            <input
              type="checkbox"
              checked={formData.intent.budget.flexible}
              onChange={(e) =>
                onNestedChange("intent.budget.flexible", e.target.checked)
              }
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="ml-2 text-sm text-gray-700">Flexible budget</span>
          </label>
        </div>

        {/* Preferences - Metals */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Preferred Metals
          </label>
          <div className="flex flex-wrap gap-2">
            {metals.map((metal) => (
              <label
                key={metal}
                className={`px-4 py-2 border-2 rounded-full cursor-pointer transition-all
                  ${
                    formData.intent.preferences.metals.includes(metal)
                      ? "border-primary bg-primary text-white"
                      : "border-gray-300 hover:border-primary"
                  }`}
              >
                <input
                  type="checkbox"
                  checked={formData.intent.preferences.metals.includes(metal)}
                  onChange={(e) => {
                    const currentMetals = formData.intent.preferences.metals;
                    const newMetals = e.target.checked
                      ? [...currentMetals, metal]
                      : currentMetals.filter((m) => m !== metal);
                    onNestedChange("intent.preferences.metals", newMetals);
                  }}
                  className="sr-only"
                />
                <span className="text-sm">{metal}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Preferences - Styles */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Preferred Styles
          </label>
          <div className="flex flex-wrap gap-2">
            {styles.map((style) => (
              <label
                key={style}
                className={`px-4 py-2 border-2 rounded-full cursor-pointer transition-all
                  ${
                    formData.intent.preferences.styles.includes(style)
                      ? "border-primary bg-primary text-white"
                      : "border-gray-300 hover:border-primary"
                  }`}
              >
                <input
                  type="checkbox"
                  checked={formData.intent.preferences.styles.includes(style)}
                  onChange={(e) => {
                    const currentStyles = formData.intent.preferences.styles;
                    const newStyles = e.target.checked
                      ? [...currentStyles, style]
                      : currentStyles.filter((s) => s !== style);
                    onNestedChange("intent.preferences.styles", newStyles);
                  }}
                  className="sr-only"
                />
                <span className="text-sm">{style}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Preferences - Categories */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Interested Categories
          </label>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <label
                key={category}
                className={`px-4 py-2 border-2 rounded-full cursor-pointer transition-all
                  ${
                    formData.intent.preferences.categories.includes(category)
                      ? "border-primary bg-primary text-white"
                      : "border-gray-300 hover:border-primary"
                  }`}
              >
                <input
                  type="checkbox"
                  checked={formData.intent.preferences.categories.includes(
                    category
                  )}
                  onChange={(e) => {
                    const currentCategories =
                      formData.intent.preferences.categories;
                    const newCategories = e.target.checked
                      ? [...currentCategories, category]
                      : currentCategories.filter((c) => c !== category);
                    onNestedChange(
                      "intent.preferences.categories",
                      newCategories
                    );
                  }}
                  className="sr-only"
                />
                <span className="text-sm">{category}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Special Requests */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Special Requests or Notes
          </label>
          <textarea
            value={formData.intent.customerNotes}
            onChange={(e) =>
              onNestedChange("intent.customerNotes", e.target.value)
            }
            rows="4"
            placeholder="Any specific requirements, designs, or questions you'd like to discuss..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>
    </div>
  );
};

// ============= CONTACT INFO SECTION =============
export const ContactInfoSection = ({ formData }) => {
  // Remove onChange prop since it's read-only

  if (!formData.selectedCustomer) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-8 text-neutral-dark">
          <FiAlertCircle className="mx-auto text-4xl mb-2" />
          <p>Please select a customer first</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center gap-2 mb-4">
        <FiUser className="text-primary text-xl" />
        <h3 className="text-lg font-semibold text-text">
          Customer Contact Information
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Full Name
          </label>
          <div className="relative">
            <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-dark" />
            <input
              type="text"
              value={formData.contactInfo.name}
              readOnly
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg bg-neutral-light cursor-not-allowed"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number
          </label>
          <div className="relative">
            <FiPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-dark" />
            <input
              type="tel"
              value={formData.contactInfo.phone}
              readOnly
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg bg-neutral-light cursor-not-allowed"
            />
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <div className="relative">
            <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-dark" />
            <input
              type="email"
              value={formData.contactInfo.email || "Not provided"}
              readOnly
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg bg-neutral-light cursor-not-allowed"
            />
          </div>
        </div>
      </div>

      <p className="text-xs text-neutral-dark mt-4 flex items-center gap-1">
        <FiInfo />
        Confirmation will be sent to these contact details
      </p>
    </div>
  );
};

// ============= CART SUMMARY SECTION =============
export const CartSummarySection = ({ cart }) => {
  if (!cart || !cart.items || cart.items.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center gap-2 mb-4">
        <FiShoppingBag className="text-primary text-xl" />
        <h3 className="text-lg font-semibold text-text">Cart Summary</h3>
      </div>

      <div className="space-y-3 mb-4">
        {cart.items.slice(0, 3).map((item, index) => (
          <div
            key={index}
            className="flex items-center gap-3 p-3 bg-neutral-light rounded-lg"
          >
            {item.product.images?.[0] && (
              <img
                src={item.product.images[0]}
                alt={item.product.name}
                className="w-16 h-16 object-cover rounded"
              />
            )}
            <div className="flex-1">
              <p className="font-medium text-text">{item.product.name}</p>
              <p className="text-sm text-neutral-dark">Qty: {item.quantity}</p>
            </div>
            <p className="font-semibold text-text">
              ₹{item.pricing?.totalPrice?.toLocaleString("en-IN")}
            </p>
          </div>
        ))}

        {cart.items.length > 3 && (
          <p className="text-sm text-neutral-dark text-center">
            +{cart.items.length - 3} more items
          </p>
        )}
      </div>

      <div className="border-t pt-4">
        <div className="flex justify-between items-center text-lg font-bold">
          <span>Total Estimated Value:</span>
          <span className="text-primary">
            ₹{cart.summary?.total?.toLocaleString("en-IN")}
          </span>
        </div>
      </div>
    </div>
  );
};

// ============= CUSTOMER SELECTION SECTION (NEW) =============
export const CustomerSelectionSection = ({
  formData,
  errors,
  onChange,
  onCustomerSelect,
}) => {
  const [showUserSelection, setShowUserSelection] = useState(false);

  // Handle user selection from UserSelection modal
  const handleUserSelected = (user) => {
    if (user) {
      onCustomerSelect(user);
      setShowUserSelection(false);
      toast.success(`Selected customer: ${user.name}`);
    }
  };

  return (
    <>
      {/* Customer Selection Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <FiUser className="text-primary text-xl" />
          <h3 className="text-lg font-semibold text-text">Select Customer</h3>
        </div>

        {formData.selectedCustomer ? (
          // ========== SELECTED CUSTOMER DISPLAY ==========
          <div className="p-4 border-2 border-green-500 bg-green-50 rounded-lg">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center">
                    ✓
                  </div>
                  <h4 className="font-semibold text-text">Selected Customer</h4>
                </div>
                <p className="text-lg font-medium text-text">
                  {formData.selectedCustomer.name}
                </p>
                <p className="text-sm text-neutral-dark">
                  📱 {formData.selectedCustomer.phoneNumber}
                </p>
                {formData.selectedCustomer.email && (
                  <p className="text-sm text-neutral-dark">
                    ✉️ {formData.selectedCustomer.email}
                  </p>
                )}
                {formData.selectedCustomer.userCode && (
                  <p className="text-xs text-neutral-dark mt-1">
                    ID: {formData.selectedCustomer.userCode}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setShowUserSelection(true)}
                className="flex items-center gap-1 px-3 py-2 text-sm text-primary hover:text-primary-dark border border-primary rounded-lg hover:bg-primary-light transition-colors"
              >
                <FiEdit className="w-4 h-4" />
                Change
              </button>
            </div>
          </div>
        ) : (
          // ========== SELECT CUSTOMER BUTTON ==========
          <div>
            <button
              type="button"
              onClick={() => setShowUserSelection(true)}
              className="w-full p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary hover:bg-primary-light hover:bg-opacity-10 transition-all group"
            >
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gray-100 group-hover:bg-primary-light flex items-center justify-center">
                  <FiUser className="w-6 h-6 text-gray-400 group-hover:text-primary" />
                </div>
                <div className="text-center">
                  <p className="text-gray-600 group-hover:text-primary font-medium mb-1">
                    Select or Create Customer
                  </p>
                  <p className="text-sm text-gray-500">
                    Click to search existing customers or create new
                  </p>
                </div>
              </div>
            </button>
            {errors.userId && (
              <p className="mt-2 text-sm text-red-500">{errors.userId}</p>
            )}
          </div>
        )}
      </div>

      {/* ========== USER SELECTION MODAL ========== */}
      <UserSelection
        isOpen={showUserSelection}
        onClose={() => setShowUserSelection(false)}
        onUserSelected={handleUserSelected}
        mode="smart"
        title="Select Customer for Appointment"
        showKYC={false}
        showAddress={false}
        showGSTIN={false}
        showAlternatePhone={false}
        showEmail={true}
        allowSearch={true}
        allowCreate={true}
        allowEdit={false}
        autoFillFromSearch={true}
      />
    </>
  );
};
